import { Paper, Stack, TableBody, TableRow, Typography } from '@mui/material';
import { flexRender, Row } from '@tanstack/react-table';

import theme from '@/theme';

import { BaseTableConfig } from './hooks/useBaseTable';
import {
  StyledTable,
  StyledTableBox,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableOverlay,
  StyledTablePagination,
  StyledTableRow,
} from './StyledTable';

type TableDataLoadingProps = {
  isLoading?: boolean;
  loadingMessage?: string;
};

type TableProcessingProps = {
  isProcessing?: boolean;
};

type BaseTableRenderProps<TData> = {
  testId?: string;
  ariaLabel?: string;
  lockFirstColumn?: boolean;
  showColumnSeparators?: boolean;
  noDataMessage?: string;
  loadingData?: TableDataLoadingProps;
  processing?: TableProcessingProps;
  onRowClick?: (e: React.MouseEvent<HTMLTableRowElement> | undefined, row: Row<TData>) => void;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  showFirstLastPageButtons?: boolean;
};

type BaseTableProps<TData> = BaseTableConfig<TData> & BaseTableRenderProps<TData>;

function BaseTable<T>(props: BaseTableProps<T>) {
  return (
    <StyledTableBox>
      {props.processing?.isProcessing && (
        <StyledTableOverlay className='base-table-overlay' data-testid='table-overlay' role='status' aria-live='polite' aria-busy='true' />
      )}
      <StyledTableContainer
        component={Paper}
        ref={props.containerRef ? props.containerRef : undefined}
        aria-busy={props.processing?.isProcessing}
        sx={{
          ...(props.processing?.isProcessing && { pointerEvents: 'none', userSelect: 'none' }),
        }}
      >
        <StyledTable data-testid={props.testId} role='table' aria-label={props.ariaLabel}>
          <StyledTableHead>
            {props.table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} data-testid='table-head' role='row'>
                {headerGroup.headers.map((header) => (
                  <StyledTableCell
                    key={header.id}
                    data-testid={`table-head-cell-${header.id}`}
                    role='columnheader'
                    scope='col'
                    isHeaderCell
                    customWidth={header.column.getSize()}
                    lockFirstColumn={props.lockFirstColumn ?? false}
                    showColumnSeparators={props.showColumnSeparators ?? false}
                    index={header.index}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </StyledTableCell>
                ))}
              </TableRow>
            ))}
          </StyledTableHead>
          <TableBody>
            {props.loadingData?.isLoading ? (
              <StyledTableRow role='row' data-testid='row-loading-data' hoverable={false}>
                <StyledTableCell align='center' colSpan={props.table.getAllLeafColumns().length} data-testid='cell-no-data'>
                  <Stack role='status' aria-live='polite'>
                    <Typography>{props.loadingData?.loadingMessage}</Typography>
                  </Stack>
                </StyledTableCell>
              </StyledTableRow>
            ) : props.table.getRowModel().rows.length === 0 ? (
              <StyledTableRow role='row' data-testid='row-no-data' hoverable={false}>
                <StyledTableCell align='center' colSpan={props.table.getAllLeafColumns().length} data-testid='cell-no-data'>
                  <Stack role='status' aria-live='polite'>
                    <Typography>{props.noDataMessage}</Typography>
                  </Stack>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              props.table.getRowModel().rows.map((row) => {
                const absoluteIndex = props.hasPagination ? props.pagination.pageIndex * props.pagination.pageSize + row.index : row.index;

                return (
                  <StyledTableRow
                    key={row.id}
                    data-testid={`row-${absoluteIndex}`}
                    role='row'
                    onClick={(e) => {
                      if (!props.onRowClick) return;

                      const selection = window.getSelection();

                      if (selection && selection.toString().length > 0) {
                        return;
                      }

                      props.onRowClick(e, row);
                    }}
                    sx={{
                      cursor: props.onRowClick ? 'pointer' : 'default',
                      '&:hover': props.onRowClick ? { backgroundColor: theme.palette.action.hover } : {},
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <StyledTableCell
                        key={cell.id}
                        data-testid={`cell-${cell.column.id}-${absoluteIndex}`}
                        role='cell'
                        customWidth={cell.column.getSize()}
                        lockFirstColumn={props.lockFirstColumn ?? false}
                        showColumnSeparators={props.showColumnSeparators ?? false}
                        index={cell.column.getIndex()}
                        scope='row'
                        sx={{
                          ...(props.onRowClick && {
                            'tr:hover &': {
                              backgroundColor: theme.hhglobal.purpose.fillWeak,
                            },
                          }),
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </StyledTableCell>
                    ))}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      {props.hasPagination && (
        <StyledTablePagination
          disabled={props.tableData.length === 0 || props.processing?.isProcessing}
          component='div'
          count={props.paginationCount}
          page={props.pagination.pageIndex}
          onPageChange={props.onPageChange}
          rowsPerPage={props.pagination.pageSize}
          rowsPerPageOptions={props.rowsPerPageOptions}
          onRowsPerPageChange={props.onRowsPerPageChange}
          data-testid='table-pagination'
          showFirstButton={props.showFirstButton}
          showLastButton={props.showLastButton}
        />
      )}
    </StyledTableBox>
  );
}

export default BaseTable;
